import supabase from '@/lib/supabase';

/**
 * 根据学校名称查询对应的学区
 * @param schoolName - 学校名称
 * @param state - 州（可选，用于多条匹配时的过滤）
 * @param city - 城市（可选，用于多条匹配时的过滤）
 * @returns 学区名称
 */
export const getDistrictBySchoolName = async (
  schoolName: string,
  state?: string,
  city?: string
): Promise<string | null> => {
  if (!schoolName) return null;

  try {
    // 从 onboard_school 表中查询学区，使用 ilike 进行模糊匹配
    const { data, error } = await supabase
      .from('onboard_school')
      .select('school_name, district, state, city')
      .ilike('school_name', schoolName);

    if (error) {
      console.error('Error fetching school district from onboard_school:', error);
      // 数据库查询出错时，fallback 到 premium school 匹配逻辑
      return getDistrictByPremiumSchool(schoolName);
    }

    if (!data || data.length === 0) {
      // 如果数据库中查不到，使用 premium school 匹配逻辑作为 fallback
      const premiumDistrict = getDistrictByPremiumSchool(schoolName);
      if (premiumDistrict) return premiumDistrict;
      return null;
    }

    // 如果只有一条记录，直接返回
    if (data.length === 1 && data[0]?.district) {
      return data[0].district;
    }

    // 如果有多条记录，使用 state 和 city 进一步过滤
    let filteredData = data;

    // 先尝试用 state 过滤
    if (state) {
      const stateFiltered = filteredData.filter(
        (record) => record.state?.toLowerCase() === state.toLowerCase()
      );
      if (stateFiltered.length > 0) {
        filteredData = stateFiltered;
      }
    }

    // 再尝试用 city 过滤
    if (city && filteredData.length > 1) {
      const cityFiltered = filteredData.filter(
        (record) => record.city?.toLowerCase() === city.toLowerCase()
      );
      if (cityFiltered.length > 0) {
        filteredData = cityFiltered;
      }
    }

    // 返回过滤后的第一条记录的学区
    if (filteredData.length > 0 && filteredData[0]?.district) {
      return filteredData[0].district;
    }

    // 如果数据库中查不到，使用 premium school 匹配逻辑作为 fallback
    const premiumDistrict = getDistrictByPremiumSchool(schoolName);
    if (premiumDistrict) return premiumDistrict;

    return null;

  } catch (error) {
    console.error('Error in getDistrictBySchoolName:', error);
    // 异常情况下，尝试使用 premium school 匹配逻辑
    return getDistrictByPremiumSchool(schoolName);
  }
};

const getDistrictByPremiumSchool = (schoolName: string): string | null => {
  const schoolLabels = ['challenger', 'basis independent', 'basis charter', 'basis'];
  const normalizedUserSchoolName = schoolName.toLowerCase();

  const labels = schoolLabels
    .filter(label => normalizedUserSchoolName.includes(label))
    .sort((a, b) => b.length - a.length);

  if (labels.length > 0) {
    return labels[0];
  } else {
    return null;
  }

}